import { connect } from 'react-redux';
import { keyBy, uniq } from 'lodash';
import { useForm } from 'react-hook-form';
import i18next from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { ENUM_MODULE_NAME } from '@commons/utils/features';
import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { getTheme } from '@commons/utils/theme';
import { isForbiddenAPIError } from '@commons/utils/errors';
import { ListView } from '@commons/utils/styledLibraryComponents';
import EmptyState from '@commons/EmptyState';
import NavigationBar from '@commons/NavigationBar';

import { getCentralKitchenStores } from '@selectors/stores';
import { getClientInfo } from '@selectors/client';

import { supplierProduct as supplierProductService } from '@services/supplierProduct';
import centralService from '@services/central';

import { CHOICES_DROPDOWN_ACTIVE } from '@admin/utils/DropdownItems';
import { fetchProduct } from '@admin/products/products/components/CreateProductModal';
import DeepsightFiltersButton from '@admin/components/FilterButton';

import {
  ADD_CENTRAL_KITCHENS_MODAL_STEPS,
  getAddCentralKitchensModalConfig,
} from './components/AddCentralKitchensModal';
import { ListViewContainer, PageContainer } from './styledComponents';
import actionsUtils from './utils/actions';
import columnsUtils from './utils/columns';

const SYNC_SOURCE_ICON = '/images/inpulse/sync-black-small.svg';

const KitchenProductSupplierProducts = (props) => {
  const {
    match: { path, params },
    client: { storeName },
    currency,
    centralKitchenStores,
    history,
    pageLoading,
    pageLoaded,
    showSuccessMessage,
    showErrorMessage,
    openGenericModal,
    refreshGenericModal,
  } = props;

  const theme = getTheme();

  // Frozen on mount like the informations tab: the data is only fetched once, so the actions
  // must keep targeting the product those data belong to
  const [productId] = useState(params.id);

  const [product, setProduct] = useState({});
  const [supplierProductRows, setSupplierProductRows] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // States filters
  const [selectedStatus, setSelectedStatus] = useState(CHOICES_DROPDOWN_ACTIVE()[0]);
  const [filters, setFilters] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(true);

  // Central kitchens available for the association modal
  const [availableCentralKitchens, setAvailableCentralKitchens] = useState([]);
  const [availableStores, setAvailableStores] = useState([]);

  // Add central kitchens modal states
  const [isAddModalOpened, setIsAddModalOpened] = useState(false);
  const [addModalStep, setAddModalStep] = useState(
    ADD_CENTRAL_KITCHENS_MODAL_STEPS.CENTRAL_KITCHENS_SELECTION,
  );
  const [selectedCentralKitchensToAdd, setSelectedCentralKitchensToAdd] = useState([]);

  // Hosts the delivered stores mappings configured by the embedded SSPM section
  const addModalForm = useForm({ defaultValues: { mappings: [] } });

  const columns = useMemo(() => columnsUtils.getColumns({ currency }), [currency]);

  const columnsFilterList = useMemo(
    () =>
      columns
        .filter(({ filterType }) => !!filterType)
        .map((column) => {
          if (column.filterType !== 'string') {
            return column;
          }

          const distinctValues = uniq(
            supplierProductRows.map((row) => row[column.propertyKey]).filter(Boolean),
          );

          return {
            ...column,
            list: distinctValues.map((value, index) => ({
              id: index,
              name: value,
              [column.propertyKey]: value,
            })),
          };
        }),
    [columns, supplierProductRows],
  );

  useEffect(() => {
    if (!productId) {
      return;
    }

    pageLoading();

    (async function loadData() {
      try {
        await fetchProduct(productId, setProduct);
        await fetchSupplierProducts();
        await fetchCentralKitchens();
      } catch (error) {
        showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FETCH_ERROR'));

        if (isForbiddenAPIError(error)) {
          history.goBack();
        }
      } finally {
        pageLoaded();
      }
    })();
  }, []);

  const fetchSupplierProducts = async () => {
    const fetchedSupplierProducts =
      await centralService.getCentralKitchenSupplierProductsByProductId(productId);

    const rows = fetchedSupplierProducts
      .filter(({ productMapping }) => productMapping.productId === productId)
      .map((supplierProduct) => {
        const invoicePackaging = (supplierProduct.packagings || []).find(
          ({ isUsedInInvoice, isActive }) => isUsedInInvoice && isActive,
        );

        return {
          id: supplierProduct.id,
          name: supplierProduct.name,
          centralKitchenName: supplierProduct.supplierName,
          supplierId: supplierProduct.supplierId,
          category: supplierProduct.category,
          subCategory: supplierProduct.subCategory,
          invoicePackagingName: invoicePackaging?.name ?? null,
          price: supplierProduct.price != null ? parseFloat(supplierProduct.price) : null,
          vatRate: supplierProduct.vatRate,
          shelfLife: supplierProduct.shelfLife,
          active: supplierProduct.active,
          isReference: !!supplierProduct.productMapping.usedByDefault,
        };
      });

    setSupplierProductRows(rows);
    setSelectedItems([]);
  };

  const fetchCentralKitchens = async () => {
    const stores = await centralService.getCentralKitchenSSPFMsOfUser();

    const activeStoresWithMappings = stores.filter(
      ({ active, supplierProfile, isKitchen }) => active && !!supplierProfile && !isKitchen,
    );

    /*
      The ListView relies on unique ids and the same store can be delivered by several central
      kitchens, so each row is keyed by the store and supplier profile pair.
    */
    const formattedStores = activeStoresWithMappings.map((store) => ({
      ...store,
      storeId: store.id,
      id: `${store.id}|${store.supplierProfile.id}`,
    }));

    setAvailableStores(formattedStores);

    if (!centralKitchenStores.length) {
      return;
    }

    const centralKitchenStoreIds = centralKitchenStores.map(({ id }) => id);

    const centralKitchenSuppliers = await centralService.getkitchenSupplierByStoreIds(
      centralKitchenStoreIds,
    );

    const centralKitchenStoresByIds = keyBy(centralKitchenStores, 'id');

    const formattedCentralKitchens = centralKitchenSuppliers.map((supplier) => {
      const associatedStoreIds = new Set(
        formattedStores
          .filter(({ supplierProfile }) => supplierProfile?.supplierId === supplier.id)
          .map(({ storeId }) => storeId),
      );

      const matchingStore = centralKitchenStoresByIds[supplier.storeId];

      return {
        ...supplier,
        value: supplier.name,
        groups: matchingStore?.groups ?? [],
        associatedStoresCount: associatedStoreIds.size,
      };
    });

    setAvailableCentralKitchens(formattedCentralKitchens);
  };

  const displayedRows = useMemo(() => {
    const statusFilteredRows = supplierProductRows.filter(
      ({ active }) => active === selectedStatus.filterValue,
    );

    if (!advancedFilters?.length) {
      return statusFilteredRows;
    }

    return advancedFilters.reduce(
      (result, { doFilter, propertyKey, value }) => doFilter(result, propertyKey, value),
      statusFilteredRows,
    );
  }, [supplierProductRows, selectedStatus, advancedFilters, applyFilters]);

  // Central kitchens that have no supplier product yet on this product, active or not
  const centralKitchensAvailableToAdd = useMemo(() => {
    const associatedSupplierIds = supplierProductRows.map(({ supplierId }) => supplierId);

    return availableCentralKitchens.filter(({ id }) => !associatedSupplierIds.includes(id));
  }, [availableCentralKitchens, supplierProductRows]);

  const deliveredStoresForSelection = useMemo(() => {
    if (!selectedCentralKitchensToAdd.length) {
      return [];
    }

    const selectedSuppliersByIds = keyBy(selectedCentralKitchensToAdd, 'id');

    return availableStores.filter(
      ({ supplierProfile }) => !!selectedSuppliersByIds[supplierProfile.supplierId],
    );
  }, [availableStores, selectedCentralKitchensToAdd]);

  const handleReloadAfterUpdate = async () => {
    pageLoading();

    try {
      await fetchProduct(productId, setProduct);
      await fetchSupplierProducts();
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FETCH_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  /*******************/
  /* Reference switch */
  /*******************/

  const handleSetReference = (supplierProductRow) => {
    openGenericModal(
      getConfirmationModal({
        title: i18next.t('ADMIN.CENTRAL_PRODUCTS.SET_REFERENCE_ACTION'),
        content: i18next.t('ADMIN.CENTRAL_PRODUCTS.SET_REFERENCE_MODAL_CONTENT', {
          centralKitchenName: supplierProductRow.centralKitchenName,
        }),
        icon: '/images/inpulse/sync-white-small.svg',
        actions: [
          GENERIC_MODAL_CANCEL_BUTTON(),
          {
            key: 1,
            color: 'inpulse-default',
            label: i18next.t('GENERAL.CONFIRM'),
            icon: '/images/inpulse/done-white-small.svg',
            handleClick: () => handleCallToSwitchReference(supplierProductRow.id),
          },
        ],
      }),
    );
  };

  const handleCallToSwitchReference = async (supplierProductId) => {
    pageLoading();

    try {
      await centralService.switchKitchenProductReference(productId, supplierProductId);

      showSuccessMessage(i18next.t('ADMIN.CENTRAL_PRODUCTS.SET_REFERENCE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.CENTRAL_PRODUCTS.SET_REFERENCE_ERROR'));
    } finally {
      pageLoaded();
    }

    await handleReloadAfterUpdate();
  };

  /****************/
  /* Deactivation */
  /****************/

  const getDeactivationModalContent = (rowsToDisable, isLastActiveDeactivation) => {
    if (isLastActiveDeactivation) {
      return i18next.t('ADMIN.CENTRAL_PRODUCTS.DEACTIVATE_LAST_MODAL_CONTENT');
    }

    if (rowsToDisable.length === 1) {
      return i18next.t('ADMIN.CENTRAL_PRODUCTS.DEACTIVATE_ONE_MODAL_CONTENT', {
        centralKitchenName: rowsToDisable[0].centralKitchenName,
      });
    }

    return i18next.t('ADMIN.CENTRAL_PRODUCTS.DEACTIVATE_MULTIPLE_MODAL_CONTENT', {
      count: rowsToDisable.length,
    });
  };

  const handleDisableSupplierProducts = (rows) => {
    const rowsToDisable = rows.filter(({ active }) => active);

    if (!rowsToDisable.length) {
      return;
    }

    const idsToDisable = new Set(rowsToDisable.map(({ id }) => id));

    // Leaving the product without any active supplier product deactivates the product itself
    const isLastActiveDeactivation = !supplierProductRows.some(
      ({ id, active }) => active && !idsToDisable.has(id),
    );

    openGenericModal(
      getConfirmationModal({
        title: i18next.t('ADMIN.CENTRAL_PRODUCTS.DEACTIVATE_CENTRAL_KITCHEN_ACTION', {
          count: rowsToDisable.length,
        }),
        content: getDeactivationModalContent(rowsToDisable, isLastActiveDeactivation),
        icon: '/images/inpulse/power-white-small.svg',
        actions: [
          GENERIC_MODAL_CANCEL_BUTTON(),
          {
            key: 1,
            color: 'inpulse-default',
            label: i18next.t('GENERAL.DEACTIVATE'),
            icon: '/images/inpulse/power-white-small.svg',
            handleClick: () =>
              handleCallToDisableSupplierProducts(rowsToDisable, isLastActiveDeactivation),
          },
        ],
      }),
    );
  };

  const handleCallToDisableSupplierProducts = async (rowsToDisable, isLastActiveDeactivation) => {
    pageLoading();

    try {
      // Deactivating the product also deactivates its supplier products
      await (isLastActiveDeactivation
        ? centralService.disableKitchenProductByIds([productId])
        : supplierProductService.disableSupplierProducts(rowsToDisable.map(({ id }) => id)));

      showSuccessMessage(
        i18next.t('ADMIN.CENTRAL_PRODUCTS.DEACTIVATE_SUCCESS', { count: rowsToDisable.length }),
      );
    } catch {
      showErrorMessage(i18next.t('ADMIN.CENTRAL_PRODUCTS.DEACTIVATE_ERROR'));
    } finally {
      pageLoaded();
    }

    await handleReloadAfterUpdate();
  };

  /*****************************/
  /* Add central kitchens flow */
  /*****************************/

  const referenceSupplierProductPrice = useMemo(
    () => supplierProductRows.find(({ isReference }) => isReference)?.price ?? null,
    [supplierProductRows],
  );

  const getAddModalConfig = () =>
    getAddCentralKitchensModalConfig({
      step: addModalStep,
      availableCentralKitchens: centralKitchensAvailableToAdd,
      selectedCentralKitchens: selectedCentralKitchensToAdd,
      setSelectedCentralKitchens: setSelectedCentralKitchensToAdd,
      deliveredStores: deliveredStoresForSelection,
      addModalForm,
      referencePrice: referenceSupplierProductPrice,
      storeName,
      handleNextStep: () =>
        setAddModalStep(ADD_CENTRAL_KITCHENS_MODAL_STEPS.DELIVERED_STORES_CONFIGURATION),
      handlePreviousStep: () =>
        setAddModalStep(ADD_CENTRAL_KITCHENS_MODAL_STEPS.CENTRAL_KITCHENS_SELECTION),
      handleSave: handleAddCentralKitchens,
    });

  const handleOpenAddCentralKitchensModal = () => {
    setAddModalStep(ADD_CENTRAL_KITCHENS_MODAL_STEPS.CENTRAL_KITCHENS_SELECTION);
    setSelectedCentralKitchensToAdd([]);
    addModalForm.setValue('mappings', []);
    setIsAddModalOpened(true);

    openGenericModal(getAddModalConfig());
  };

  useEffect(() => {
    if (!isAddModalOpened) {
      return;
    }

    refreshGenericModal(getAddModalConfig());
  }, [isAddModalOpened, addModalStep, selectedCentralKitchensToAdd, deliveredStoresForSelection]);

  const handleAddCentralKitchens = async () => {
    pageLoading();

    setIsAddModalOpened(false);

    try {
      const centralKitchenStoreIds = selectedCentralKitchensToAdd.map(({ storeId }) => storeId);

      // Mappings configured through the embedded SSPM section (defaults kept when untouched)
      const storeSupplierProductMappings = (addModalForm.getValues('mappings') || []).map(
        ({ storeId, supplierProfile, hasStock, available, hasDlc }) => ({
          storeId,
          supplierProfile,
          hasStock: !!hasStock,
          available: !!available,
          hasDlc: !!hasDlc,
        }),
      );

      await centralService.addCentralKitchensToKitchenProduct(
        productId,
        centralKitchenStoreIds,
        storeSupplierProductMappings,
      );

      showSuccessMessage(i18next.t('ADMIN.CENTRAL_PRODUCTS.ADD_CENTRAL_KITCHENS_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.CENTRAL_PRODUCTS.ADD_CENTRAL_KITCHENS_ERROR'));
    } finally {
      pageLoaded();
    }

    await handleReloadAfterUpdate();
  };

  const actions = actionsUtils.getGlobalActions({
    selectedItems,
    hasCentralKitchensAvailableToAdd: !!centralKitchensAvailableToAdd.length,
    handleOpenAddCentralKitchensModal,
    handleDisableSupplierProducts,
  });

  const rowActions = actionsUtils.getRowActions({
    handleSetReference,
    handleDisableSupplierProducts,
  });

  return (
    <>
      <NavigationBar
        module={ENUM_MODULE_NAME.KITCHEN_PRODUCT_DETAILS}
        path={path}
        product={{ name: product.name, id: productId }}
        storeName={storeName}
        bigTopBar
        displaySubFeatures
        enableActionBottomOrder
      />
      <PageContainer>
        <ListViewContainer>
          <ListView
            actions={actions}
            columns={columns}
            data={displayedRows}
            defaultOrderBy={'centralKitchenName'}
            defaultOrderType={'asc'}
            markerConfiguration={{
              isHidden: ({ isReference }) => !isReference,
              backgroundColor: theme.colors.brand.primary,
              icon: { src: SYNC_SOURCE_ICON },
            }}
            minActionsInActionsDropdown={1}
            padding={'24px 24px 0px 24px'}
            renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
            renderFilterButton={() => (
              <DeepsightFiltersButton
                advancedFilters={advancedFilters}
                applyFilters={applyFilters}
                columnsFilterList={columnsFilterList}
                customSingleDropDowns={[
                  {
                    id: 'active-inactive',
                    selectedItem: selectedStatus,
                    list: CHOICES_DROPDOWN_ACTIVE(),
                    setSelectedItem: setSelectedStatus,
                    defaultSelectedItem: CHOICES_DROPDOWN_ACTIVE()[0],
                    itemSelectedIcon: '/images/inpulse/power-ip-black.svg',
                  },
                ]}
                filters={filters}
                setAdvancedFilters={setAdvancedFilters}
                setApplyFilters={setApplyFilters}
                setFilters={setFilters}
                textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
              />
            )}
            rowActions={rowActions}
            setSelectedItems={setSelectedItems}
          />
        </ListViewContainer>
      </PageContainer>
    </>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
  centralKitchenStores: getCentralKitchenStores(state.baseReducer.activeStores),
});

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(KitchenProductSupplierProducts);
